import { useCreateSystemInvoiceMutation } from "./useCreateSystemInvoiceMutation";
import { ICreateInvoicePayload } from "../interfaces/invoice.interfaces";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { AxiosError } from "axios";
import { useModals } from "../../../../context/ModalContext";

export const useCreateSystemInvoice = () => {
  const createSystemInvoiceMutation = useCreateSystemInvoiceMutation();
  const { showSnackbar } = useGlobalSnackbar();
  const { closeByKey } = useModals();

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;
      const paypalErrorDetails = error.response?.data?.data?.details?.[0]?.issue || error.response?.data?.data?.message;

      if (paypalErrorDetails) {
        return `PayPal Error: ${paypalErrorDetails}`;
      }

      switch (status) {
        case 400:
          return message || "Invalid system invoice data. Please check your input and try again.";
        case 401:
          return "You are not authorized to create invoices. Please log in again.";
        case 403:
          return "You don't have permission to create invoices for this company.";
        case 422:
          return message || "Validation error. Please check all required fields.";
        case 500:
          return "Server error occurred. Please try again later or contact support.";
        case 502:
          return message || "The invoice could not be sent by email, so it was not created. Please try again.";
        default:
          return message || "An unexpected error occurred. Please try again.";
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return "An unexpected error occurred. Please try again.";
  };

  const createSystemInvoice = (invoice: ICreateInvoicePayload, options?: { invoiceModal?: string }) => {
    createSystemInvoiceMutation.mutate(invoice, {
      onSuccess: () => {
        showSnackbar("System invoice created successfully! Payment link has been generated.", "success", 5000, "top-center");
        if (options?.invoiceModal) {
          closeByKey(options.invoiceModal);
        }
      },
      onError: (error) => {
        console.error("Error creating system invoice:", error);
        const errorMessage = getErrorMessage(error);
        showSnackbar(errorMessage, "error", 6000, "top-center");
      },
    });
  };

  return {
    createSystemInvoice,
    isPending: createSystemInvoiceMutation.isPending,
    isError: createSystemInvoiceMutation.isError,
    isSuccess: createSystemInvoiceMutation.isSuccess,
    error: createSystemInvoiceMutation.error,
    data: createSystemInvoiceMutation.data,
  };
};
